import { EmptyState, Text } from '@payaca/ambrosia';
import {
  getAddressCoordinates,
  type StoredCoordinates,
} from '@payaca/helpers/locationHelper';
import { clstx } from '@payaca/utilities/cvaMerge';
import type { Nullish } from '@payaca/utilities/types';
import { FC } from 'react';

import {
  DEFAULT_STATIC_MAP_SIZE,
  getStaticMapUrl,
  type StaticMapSize,
} from './staticMapUrl';
import SiteLocationMarker from './SiteLocationMarker';

export type SiteLocationThumbnailProps = {
  coordinates: Nullish<StoredCoordinates>;
  addressText: Nullish<string>;
  onActivate?: () => void;
};

const frameClassName =
  'relative aspect-video w-full overflow-hidden rounded-lg border border-gray-200';

const staticMapSizes: Array<StaticMapSize & { descriptor: string }> = [
  { width: 320, height: 180, descriptor: '640w' },
  { ...DEFAULT_STATIC_MAP_SIZE, descriptor: '960w' },
  { width: 640, height: 360, descriptor: '1280w' },
];

const SiteLocationThumbnail: FC<SiteLocationThumbnailProps> = (props) => {
  const { coordinates, addressText, onActivate } = props;

  const usableCoordinates = getAddressCoordinates(coordinates);
  const mapSources = usableCoordinates
    ? staticMapSizes.flatMap(({ descriptor, ...size }) => {
        const url = getStaticMapUrl(usableCoordinates, size);
        return url ? [{ descriptor, url }] : [];
      })
    : [];
  const mapUrl = mapSources.find(
    ({ descriptor }) => descriptor === '960w'
  )?.url;

  if (!mapUrl || !usableCoordinates) {
    return (
      <div
        className={clstx(frameClassName, 'flex items-center justify-center')}
      >
        <EmptyState
          iconName="marker-pin-01.3"
          text="We do not know exactly where this site is"
          className="px-4 text-center"
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      className={clstx(
        frameClassName,
        'group block cursor-pointer p-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500'
      )}
      onClick={onActivate}
      aria-label={
        addressText
          ? `View larger site location map for ${addressText}`
          : 'View larger site location map'
      }
    >
      <img
        className="h-full w-full object-cover"
        src={mapUrl}
        srcSet={mapSources
          .map(({ descriptor, url }) => `${url} ${descriptor}`)
          .join(', ')}
        sizes="(min-width: 1601px) 464px, (min-width: 1025px) 384px, (min-width: 768px) 336px, calc(100vw - 3rem)"
        alt=""
        loading="lazy"
      />
      <SiteLocationMarker />
      <div className="pointer-events-none absolute inset-0 flex items-end justify-center bg-linear-to-t from-black/60 via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
        <div className="mb-3 rounded bg-white/90 px-2 py-1">
          <Text as="span">View larger</Text>
        </div>
      </div>
    </button>
  );
};

export default SiteLocationThumbnail;
